/**
 * The session store: a thin external store over the pure reducer, plus the narrow selectors
 * components subscribe through.
 *
 * All the state is one object under `session`, deliberately. A component that only needs the status
 * subscribes to the status; nesting keeps `dispatch` out of the reducer's own state shape, so the
 * reducer stays exactly as testable as it is in `sessionMachine.test.ts` — there is no store-shaped
 * variant of it to keep in sync.
 *
 * Selectors are exported as hooks rather than as raw store access because the narrow-selector rule
 * is easier to follow than to remember: `useSessionStatus()` cannot accidentally subscribe a screen
 * to the whole session the way `useSessionStore((s) => s.session)` can.
 */

import { create } from 'zustand';

import {
  INITIAL_SESSION_STATE,
  sessionReducer,
  type SessionEvent,
  type SessionState,
  type SessionStatus,
  type SessionUser,
} from '../model/sessionMachine';

interface SessionStore {
  session: SessionState;
  /** The only way state changes. Every caller reports a fact; the reducer decides what it means. */
  dispatch: (event: SessionEvent) => void;
}

export const useSessionStore = create<SessionStore>()((set) => ({
  session: INITIAL_SESSION_STATE,
  dispatch: (event) =>
    set((store) => {
      const next = sessionReducer(store.session, event);
      // Identity check: the reducer returns the same object for a guarded no-op, and passing that
      // straight to `set` would still notify every subscriber.
      return next === store.session ? store : { session: next };
    }),
}));

/** Read the session outside React — route guards and the controller both need this. */
export function readSession(): SessionState {
  return useSessionStore.getState().session;
}

export function dispatchSessionEvent(event: SessionEvent): void {
  useSessionStore.getState().dispatch(event);
}

export function useSessionStatus(): SessionStatus {
  return useSessionStore((store) => store.session.status);
}

export function useSessionUser(): SessionUser | null {
  return useSessionStore((store) => store.session.user);
}

/**
 * Call `listener` with the user id whose session just ENDED — every way one can: a deliberate
 * sign-out, a Supabase `SIGNED_OUT` after a refresh failed, a refusal, or another account's
 * hydration replacing this one without a sign-out in between. Read off the store rather than off
 * the events, so a new path to `user: null` cannot be added without it firing.
 *
 * It does NOT fire on a degraded session, which keeps its user by design, nor on a boot that never
 * had one. The one listener today is the plan builder's crash recovery (`app/main.tsx`), which must
 * take the ending trainer's unsaved plan out of this tab before anybody else signs in here.
 */
export function onSessionEnd(listener: (endedUserId: string) => void): () => void {
  return useSessionStore.subscribe((store, previous) => {
    const ended = previous.session.user?.id;
    if (ended !== undefined && store.session.user?.id !== ended) listener(ended);
  });
}
